import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Remove location import
import { User } from '../../../types';
import { apiService, ApiService } from '../../../services/apiService';
import { API_ENDPOINTS } from '../../../config/api.config';

interface VerifyUserResponse {
    user: User;
    token: string;
    refreshToken: string;
    sessionId: string;
}

// Move this outside the hook to prevent recreation
const getInitialUser = (): User | null => {
    try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            console.log("Initial user from localStorage:", {
                role: parsedUser.role,
                isSystemOrg: parsedUser.isSystemOrg,
                organizationId: parsedUser.organizationId
            });
            return parsedUser;
        }
    } catch {
        console.error('Failed to parse stored user');
    }
    return null;
};

export const useAuth = () => {
    const navigate = useNavigate();
    const {
        isAuthenticated: isAuth0Authenticated,
        user: auth0User,
        isLoading: auth0Loading,
        getAccessTokenSilently,
        logout: auth0Logout,
        loginWithRedirect
    } = useAuth0();

    const [user, setUser] = useState<User | null>(getInitialUser);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (auth0Loading || isInitialized) {
            return;
        }

        if (!isAuth0Authenticated || !auth0User?.email) {
            console.log("No Auth0 authentication, clearing state");
            localStorage.removeItem('user');
            localStorage.removeItem('auth0_access_token');
            setUser(null);
            setIsLoading(false);
            setIsInitialized(true);
            return;
        }

        const initializeAuth = async () => {
            try {
                console.log("Initializing authentication");
                setIsLoading(true);
                ApiService.setTokenGetter(getAccessTokenSilently);

                const auth0Token = await getAccessTokenSilently();
                console.log("Got Auth0 token, verifying with backend");
                localStorage.setItem('auth0_access_token', auth0Token);

                const response = await apiService.post<VerifyUserResponse>(
                    API_ENDPOINTS.AUTH.AUTH0.VERIFY_TOKEN,
                    { token: auth0Token }
                );

                if (response?.user) {
                    console.log("Received user data:", {
                        role: response.user.role,
                        isSystemOrg: response.user.isSystemOrg,
                        organizationId: response.user.organizationId
                    });
                    setUser(response.user);
                    localStorage.setItem('user', JSON.stringify(response.user));
                    localStorage.setItem('access_token', response.token);
                    localStorage.setItem('refresh_token', response.refreshToken);
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                localStorage.removeItem('user');
                localStorage.removeItem('auth0_access_token');
                setUser(null);
                navigate('/login', { replace: true });
            } finally {
                setIsLoading(false);
                setIsInitialized(true);
            }
        };

        initializeAuth();
    }, [isAuth0Authenticated, auth0User, auth0Loading, getAccessTokenSilently, navigate, isInitialized]);

    const handleLogout = useCallback(() => {
        console.log("Logging out, clearing state");
        localStorage.removeItem('user');
        localStorage.removeItem('auth0_access_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        auth0Logout();
    }, [auth0Logout]);

    return {
        user,
        isLoading: isLoading || auth0Loading,
        isAuthenticated: !!user && isAuth0Authenticated,
        login: loginWithRedirect,
        logout: handleLogout
    };
};